/* Getting a file out of the app and onto the disk.
 *
 * This is the export the spec calls the real backup (§6) and the only copy that
 * survives a lost passphrase, and in the macOS shell it did nothing at all.
 * Silently: no error, no message, no file. The button was pressed and the app
 * looked like it had worked.
 *
 * ── why the browser way cannot work there ────────────────────────────────
 *
 * It built a `blob:` URL and clicked an anchor carrying `download`. Two things
 * stop that inside the desktop shell, and either alone is enough:
 *
 * The window's content policy is `default-src 'self'` (`tauri.conf.json`).
 * `blob:` is not among the sources it allows, and adding it would widen the
 * policy for every other thing on the page to fix one button.
 *
 * The webview has no download handling. `download` on an anchor is a request to
 * the browser chrome to save a response, and there is no chrome here: the
 * element behaves as though the attribute were not written. Nothing throws,
 * because nothing failed — the click was delivered to something that does not
 * do anything.
 *
 * ── how it works instead ─────────────────────────────────────────────────
 *
 * The same trick the broker fetch uses, and by the same reasoning: the shell
 * does natively what the page cannot, reached through the bridge Tauri already
 * injects, with no import anywhere in `src/` and §2's four dependencies intact.
 * `save_export` in `src-tauri/src/main.rs` writes the file and hands back where
 * it put it, so the screen can name the path rather than say "done" about
 * something the user then has to go and find.
 *
 * The browser path is kept for a browser, and repaired while it was open: the
 * anchor is now put into the document before it is clicked, because a detached
 * one does nothing in Firefox, and the object URL is released on a later tick
 * instead of on the line after the click, which is a race the download can
 * lose. Both were latent — this book is used in Chrome and Safari, where the
 * old code happened to work.
 *
 * Android is deliberately not claimed. Capacitor's webview needs a download
 * listener the same way, and nothing here has been run against it, so the shell
 * reports what it managed rather than asserting a file exists. */

type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

/** The desktop shell's bridge. Read off the global rather than imported, for
    the reason given in `brokerLink.ts`: an import would be a dependency (§2). */
function desktopInvoke(): Invoke | null {
  const bridge = (globalThis as { __TAURI__?: { core?: { invoke?: Invoke } } }).__TAURI__;
  return typeof bridge?.core?.invoke === 'function' ? bridge.core.invoke : null;
}

export interface Saved {
  /** Where it went, when the shell can say. Null in a browser, which hands the
      file to the download machinery and is not told the outcome. */
  path: string | null;
}

/** How long to hold the object URL open after the click. Long enough for the
    download to have been handed off, short enough not to matter. */
const RELEASE_AFTER_MS = 60_000;

/** Saves `contents` as `name`. Throws with the shell's own words when it
    cannot, so a failure says something rather than nothing. */
export async function saveTextFile(name: string, contents: string): Promise<Saved> {
  const invoke = desktopInvoke();

  if (invoke) {
    const path = await invoke('save_export', { name, contents });
    return { path: typeof path === 'string' ? path : null };
  }

  const blob = new Blob([contents], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  // Attached before the click: a detached anchor is inert in Firefox.
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Released later, not on the next line. Revoking immediately can pull the
  // blob out from under a download that has not started reading it yet.
  setTimeout(() => URL.revokeObjectURL(url), RELEASE_AFTER_MS);
  return { path: null };
}

/** What the screen says once the file is away. Names the path where there is
    one, because "Exported." about a file whose location is unstated leaves the
    user hunting for their only backup. */
export function savedMessage(saved: Saved): string {
  return saved.path === null ? 'Exported.' : `Saved to ${saved.path}`;
}
